import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RecordActionsMenu } from "@/components/RecordActions";
import { FileStack, ShieldCheck, CheckCircle2, RefreshCw, Eye, Upload, FlaskConical, Loader2 } from "lucide-react";

const TSTYLE = { Imported: "bg-slate-100 text-slate-600", Approved: "bg-emerald-100 text-emerald-800", Superseded: "bg-slate-200 text-slate-600", Archived: "bg-slate-100 text-slate-500" };

export default function SSPTemplateLibrary({ me }) {
  const [templates, setTemplates] = useState([]);
  const [detail, setDetail] = useState(null);
  const [testing, setTesting] = useState("");
  const fileRef = useRef();
  const isSuper = me?.recon_role === "super_admin";

  const load = useCallback(() => { api.get("/ssp/templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const reimport = async () => {
    try { const r = await api.post("/ssp/templates/import"); toast.success(`Imported ${r.data.imported} master template(s).`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
  };
  const runTest = async (t) => {
    setTesting(t.id);
    try { const r = await api.post(`/ssp/templates/${t.id}/test`); toast.success(`Test OK — opens=${r.data.opens}, replaced ${r.data.replaced}, ${r.data.raw_left} raw left, ${r.data.tables} tables.`); }
    catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(""); }
  };
  const setStatus = async (t, status) => {
    try { await api.post(`/ssp/templates/${t.id}/review`, { status }); toast.success(`Template → ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post("/ssp/templates/upload", fd); toast.success(`Uploaded master template v${r.data.version}.`); load(); }
    catch (er) { toast.error(er.response?.data?.detail || "Upload failed"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <section className="rounded-md border border-border bg-card" data-testid="ssp-template-library">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2"><FileStack className="h-4 w-4 text-gold-dark" /><h2 className="font-display font-bold text-navy">SSP Template Library</h2></div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500"><ShieldCheck className="h-4 w-4" /> {isSuper ? "Super Admin" : "Read-only"}</span>
          {isSuper && <><input ref={fileRef} type="file" accept=".docx" className="hidden" onChange={upload} data-testid="ssp-tpl-upload-input" />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="ssp-tpl-upload"><Upload className="h-3.5 w-3.5 mr-1.5" /> Upload Master</Button>
            <Button size="sm" variant="outline" onClick={reimport} data-testid="ssp-tpl-reimport"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Import Default</Button></>}
        </div>
      </div>
      <div className="p-5 space-y-3">
        <p className="text-sm text-muted-foreground">The ComplyX Gov master SSP template establishes the house style. The original is preserved immutably in object storage; uploading a replacement creates a new version and supersedes the previous one. Templates are imported as <b>Imported</b> and must be tested and approved by a Super Admin before being used to generate SSPs.</p>
        <div className="rounded border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b text-left">{["Template", "Ver", "Placeholders", "Hash", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5 font-semibold text-navy text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
            <tbody>
              {templates.length === 0 ? <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground text-sm">No master template. Import the default or upload one.</td></tr> :
                templates.map((t) => (
                <tr key={t.id} className="border-b border-border last:border-0 hover:bg-slate-50" data-testid="ssp-tpl-row">
                  <td className="px-3 py-2.5 font-medium text-navy">{t.title}</td>
                  <td className="px-3 py-2.5 text-xs">{t.version}</td>
                  <td className="px-3 py-2.5 text-xs">{t.placeholder_count}</td>
                  <td className="px-3 py-2.5 text-[10px] font-mono">{(t.file_hash || "").slice(0, 12)}…</td>
                  <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded text-[10px] font-medium ${TSTYLE[t.status] || "bg-slate-100"}`}>{t.status}</span></td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="flex items-center gap-0.5">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setDetail(t)} data-testid={`ssp-tpl-view-${t.id}`}><Eye className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" disabled={testing === t.id} onClick={() => runTest(t)} title="Run test generation" data-testid={`ssp-tpl-test-${t.id}`}>{testing === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}</Button>
                      {isSuper && t.status === "Imported" && <Button size="sm" variant="outline" onClick={() => setStatus(t, "Approved")} data-testid={`ssp-tpl-approve-${t.id}`}>Approve</Button>}
                      {isSuper && t.status === "Approved" && <Button size="sm" variant="outline" onClick={() => setStatus(t, "Superseded")}>Supersede</Button>}
                      <RecordActionsMenu type="ssp_template" id={t.id} onChanged={load} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {detail && (
        <Dialog open onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="ssp-tpl-detail">
            <DialogHeader><DialogTitle>{detail.title}</DialogTitle><DialogDescription>{detail.filename} · v{detail.version} · {detail.status}</DialogDescription></DialogHeader>
            <div className="text-sm space-y-2">
              <div className="text-xs text-muted-foreground">SHA-256: <span className="font-mono">{detail.file_hash}</span></div>
              <div className="text-xs text-muted-foreground">Detected placeholders ({detail.placeholder_count})</div>
              <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto">{(detail.placeholders || []).map((p) => <span key={p} className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-mono">{p}</span>)}</div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
